/** Injected by scripts/build-boot-bundles.mjs at bundle time. */
declare const __DRIPPY_BUILD_VERSION__: string | undefined

const DEFAULT_RETRIES = 3
const DEFAULT_BASE_DELAY_MS = 500

const isRetryableError = (error: Error): boolean => {
	const msg = error.message
	return (
		/Failed to fetch dynamically imported module/i.test(msg) ||
		/Importing a module script failed/i.test(msg) ||
		/error loading dynamically imported module/i.test(msg) ||
		/ERR_HTTP2_PROTOCOL_ERROR/i.test(msg) ||
		/truncated/i.test(msg) ||
		/incomplete/i.test(msg) ||
		/timed out/i.test(msg) ||
		/abort/i.test(msg) ||
		/network/i.test(msg)
	)
}

const sleep = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms))

const resolveUrl = (specifier: string): string => {
	try {
		// Root-absolute paths must stay rooted at origin so nested routes
		// (e.g. `/u/@user`) do not resolve `./elements/...` under `/u/`.
		if (specifier.startsWith('/')) return new URL(specifier, location.origin).href
		return new URL(specifier, document.baseURI).href
	} catch {
		return specifier
	}
}

/**
 * Fetch a large same-origin ESM bundle to verify the full body was received, then
 * import the real URL (import maps do not apply to blob: module URLs).
 * Cloudflare↔Railway HTTP/2 can return HTTP 200 with a truncated body.
 */
export async function importLargeModuleWithRetry<T extends object>(
	specifier: string,
	options?: {retries?: number; baseDelayMs?: number; minBytes?: number; fetchTimeoutMs?: number},
): Promise<T> {
	const retries = options?.retries ?? DEFAULT_RETRIES
	const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS
	const minBytes = options?.minBytes ?? 1_500_000
	const fetchTimeoutMs = options?.fetchTimeoutMs ?? 20_000
	const url = resolveUrl(specifier)
	let lastError: Error = new Error(`Failed to load ${url}`)

	for (let attempt = 0; attempt < retries; attempt++) {
		try {
			const res = await fetch(url, {
				cache: 'no-store',
				credentials: 'same-origin',
				signal: AbortSignal.timeout(fetchTimeoutMs),
			})
			if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`)

			const expectedLength = Number(res.headers.get('content-length') || 0)
			const body = await res.arrayBuffer()
			const received = body.byteLength

			if (received < minBytes) throw new Error(`Incomplete bundle (${received} bytes, need ≥ ${minBytes}) for ${url}`)
			if (expectedLength > 0 && received !== expectedLength)
				throw new Error(`Truncated bundle (${received}/${expectedLength} bytes) for ${url}`)

			// Import the real module URL so the page import map resolves meteor/* and vendor/*.
			// Blob URLs cannot use import maps, which breaks bundled externals in home-page.bundle.js.
			return (await import(/* @vite-ignore */ url)) as T
		} catch (err) {
			lastError = err instanceof Error ? err : new Error(String(err))
			if (attempt >= retries - 1 || !isRetryableError(lastError)) break
			await sleep(baseDelayMs * (attempt + 1))
		}
	}

	throw lastError
}

/** Dynamic import with exponential back-off for transient HTTP/2 failures. */
export async function importWithRetry<T extends object>(
	specifier: string,
	options?: {retries?: number; baseDelayMs?: number},
): Promise<T> {
	const retries = options?.retries ?? DEFAULT_RETRIES
	const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS
	let lastError: Error = new Error(`Failed to import ${specifier}`)

	for (let attempt = 0; attempt < retries; attempt++) {
		try {
			return (await import(/* @vite-ignore */ specifier)) as T
		} catch (err) {
			lastError = err instanceof Error ? err : new Error(String(err))
			if (attempt >= retries - 1 || !isRetryableError(lastError)) break
			await sleep(baseDelayMs * (attempt + 1))
		}
	}

	throw lastError
}

/** Cache-bust a same-origin module URL using the build's git commit hash. */
export const versionedModuleUrl = (relativePath: string): string => {
	const version = typeof __DRIPPY_BUILD_VERSION__ === 'string' ? __DRIPPY_BUILD_VERSION__.trim() : ''
	if (!version) return relativePath
	const sep = relativePath.includes('?') ? '&' : '?'
	return `${relativePath}${sep}v=${encodeURIComponent(version)}`
}
